/**
 * Django Formset Manager with Alpine.js
 * - Hides deleted forms (without removing them)
 * - Always creates new forms, does not recycle deleted forms
 * - Always moves a restored form to the end
 * - Counter = visible forms
 * - Respects MIN_NUM_FORMS and MAX_NUM_FORMS
 */

document.addEventListener('alpine:init', () => {
    Alpine.data('formsetManager', (formsetName) => ({
        formsetName,
        deletedForms: new Set(),
        visibleFormsCount: 0,
        allowsAdd: false,

        /* ---------------- INIT ---------------- */

        init() {
            this.hideDeleteCheckboxes();
            this.reindexVisibleForms();
            this.updateStates();
        },

        /* ---------------- HELPERS ---------------- */

        getContainer() {
            return document.querySelector(`[data-formset-container="${this.formsetName}"]`);
        },

        getManagement() {
            return document.querySelector(`[data-formset-management="${this.formsetName}"]`);
        },

        getTemplate() {
            return this.getManagement()?.querySelector('template');
        },

        getTotalFormsInput() {
            return this.getManagement().querySelector(`[name="${this.formsetName}-TOTAL_FORMS"]`);
        },

        getMaxForms() {
            const input = this.getManagement()?.querySelector(`[name="${this.formsetName}-MAX_NUM_FORMS"]`);
            return input ? parseInt(input.value) : 1000;
        },

        getMinForms() {
            const input = this.getManagement()?.querySelector(`[name="${this.formsetName}-MIN_NUM_FORMS"]`);
            return input ? parseInt(input.value) : 0;
        },

        getVisibleForms() {
            return [...this.getContainer().children].filter(el => !el.classList.contains('hidden'));
        },

        updateStates() {
            this.visibleFormsCount = this.getVisibleForms().length;
            this.allowsAdd = this.canAddMore();
            this.updateDeleteButtonsVisibility();
        },

        canAddMore() {
            return this.getVisibleForms().length < this.getMaxForms();
        },

        canDelete() {
            return this.getVisibleForms().length > this.getMinForms();
        },

        resetForm(form) {
            form.querySelectorAll('input, textarea, select').forEach(el => {
                if (el.name?.endsWith('-id') || el.name?.endsWith('-DELETE')) return;
                if (el.type === 'checkbox' || el.type === 'radio') el.checked = false;
                else el.value = '';
            });
        },

        hideDeleteCheckboxes() {
            this.getContainer()
                .querySelectorAll('[id$="-DELETE"], label[for$="-DELETE"]')
                .forEach(el => {
                    if (el.tagName === 'INPUT') {
                        el.type = 'hidden';
                        el.closest('.form-check, .form-group')?.classList.add('hidden');
                    } else {
                        el.classList.add('hidden');
                    }
                });
        },

        /* ---------------- UI ---------------- */

        reindexVisibleForms(selectorBadge = '[data-form-index-badge]', callback = null) {
            this.getVisibleForms().forEach((form, idx) => {
                form.querySelectorAll(selectorBadge).forEach(el => {
                    if (callback && typeof callback === 'function') {
                        callback(el, idx, form);
                    } else {
                        el.textContent = idx;
                    }
                });
            });
            this.updateStates();
        },

        updateDeleteButtonsVisibility() {
            const deletable = this.canDelete();
            this.getVisibleForms().forEach(form => {
                const btn = form.querySelector('[data-delete-btn]');
                if (btn) btn.style.display = deletable ? '' : 'none';
            });
        },

        /* ---------------- ADD ---------------- */

        addForm() {
            if (!this.canAddMore()) return;

            const template = this.getTemplate();
            if (!template) return;

            const index = parseInt(this.getTotalFormsInput().value);
            const html = template.innerHTML.replace(/__prefix__/g, index);

            const wrapper = document.createElement('div');
            wrapper.innerHTML = html;

            const newForm = wrapper.firstElementChild;
            newForm.dataset.formIndex = index;

            this.getContainer().appendChild(newForm);
            this.getTotalFormsInput().value = index + 1;

            this.hideDeleteCheckboxes();
            this.reindexVisibleForms();
        },

        /* ---------------- DELETE ---------------- */

        toggleDeleteFromEvent(event) {
            const form = event.target.closest('[data-form-index]');
            if (!form) return;
            this.toggleDelete(parseInt(form.dataset.formIndex));
        },

        toggleDelete(formIndex) {
            if (!this.canDelete()) return;

            const form = this.getContainer().querySelector(`[data-form-index="${formIndex}"]`);
            if (!form) return;

            const checkbox = form.querySelector('input[id$="-DELETE"]');
            if (!checkbox) return;

            checkbox.value = 'on';

            form.querySelectorAll('input:not([id$="-DELETE"]):not([id$="-id"]), textarea, select')
                .forEach(el => el.disabled = true);

            form.classList.add('hidden');

            this.reindexVisibleForms();
        },
    }));
});
